"use client";

import {
  AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { supabase } from "@/lib/supabase";
import { useEffect, useState } from "react";
import { toast } from "sonner";

type AccountAccessDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName: string;
  confirmText: string;
  active: boolean;
  onGoToAgents: () => void;
  onChanged: () => void;
};

export function AccountAccessDialog({
  open, onOpenChange, userId, userName, confirmText, active, onGoToAgents, onChanged,
}: AccountAccessDialogProps) {
  const [typed, setTyped] = useState("");
  const [changing, setChanging] = useState(false);
  const [agentCount, setAgentCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    async function loadAgents() {
      setTyped("");
      setAgentCount(null);
      setLoadError(false);
      if (!active) return;
      setLoading(true);
      const { data, error } = await supabase.rpc("list_user_owned_agents", { p_user_id: userId });
      if (cancelled) return;
      if (error) {
        console.error("[AccountAccessDialog] Handover check failed:", error);
        toast.error("Failed to check agent ownership. You can still deactivate access.");
        setLoadError(true);
      } else {
        setAgentCount(data?.length ?? 0);
      }
      setLoading(false);
    }
    void loadAgents();
    return () => { cancelled = true; };
  }, [active, open, userId]);

  async function handleChangeAccess() {
    if (changing || typed.trim() !== confirmText) return;
    setChanging(true);
    const { error } = await supabase.rpc("set_account_active", { p_user_id: userId, p_active: !active });
    setChanging(false);
    if (error) {
      console.error("[AccountAccessDialog] Account access change failed:", error);
      toast.error(error.message || "Failed to change account access.");
      return;
    }
    toast.success(active ? "Account deactivated." : "Account reactivated.");
    onOpenChange(false);
    onChanged();
  }

  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!changing) onOpenChange(next); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{active ? "Deactivate" : "Reactivate"} {userName}?</AlertDialogTitle>
          <AlertDialogDescription>
            {active
              ? "This immediately blocks access and revokes sessions and API keys. Chats, agents, memberships and history are retained. User-owned schedules are disabled."
              : "This restores account access. The user must sign in again. Revoked credentials and disabled schedules are not restored."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {active && (
          <div className="space-y-2">
            {loading && <Spinner className="size-4 text-muted-foreground" />}
            {loadError && <p className="text-sm text-muted-foreground">Agent ownership could not be checked. Review handover separately.</p>}
            {!!agentCount && (
              <>
                <p className="text-sm text-muted-foreground">
                  This user owns {agentCount} {agentCount === 1 ? "agent" : "agents"}, including archived agents.
                  Transfer shared agents to an active owner as part of handover. This does not block deactivation.
                </p>
                <Button type="button" size="sm" variant="outline" disabled={changing} onClick={() => { onOpenChange(false); onGoToAgents(); }}>
                  Go to Agents
                </Button>
              </>
            )}
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="account-access-confirm">
            Type <span className="font-mono text-xs">{confirmText}</span> to confirm
          </Label>
          <Input id="account-access-confirm" value={typed} onChange={(event) => setTyped(event.target.value)}
            autoComplete="off" maxLength={320} disabled={changing} />
        </div>
        <AlertDialogFooter>
          <Button type="button" variant="outline" disabled={changing} onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" variant={active ? "destructive" : "default"}
            disabled={changing || typed.trim() !== confirmText} onClick={() => void handleChangeAccess()}>
            {changing && <Spinner className="size-3.5" />}
            {changing ? (active ? "Deactivating…" : "Reactivating…") : (active ? "Deactivate account" : "Reactivate account")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
